import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
	setForgotPasswordStatus,
	getAuthorizationStatus,
	setAuthorizationStatus,
	getVideoChatMode,
	setVideoChatMode,
	getFilterMode,
	setFilterMode,
	setMapMode,
	getErocontentAccessStatus,
	setErocontentAccessStatus,
	getAviableForUsersStatus,
	setAviableForUsersStatus,
	setAuthorisationAccessType,
	getAuthorisationAccessType,
} from '@/stores/slices/popupSlice';
import { Button, ButtonLink, Icon, InputInLabel } from '@/components/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string, mixed } from 'yup';
import axios from 'axios';
import { makeRequest } from '@/services/makeRequest';
import './authorization.scss';
import { setUserType, setUserId } from '@/stores/slices/userSlice';
import { setBurgerMenu } from '@/stores/slices/headerSlice';
import { useEffect, useState } from 'react';
import baseUrl from '@/services/apiConfig';
import { setAfterAuth } from '@/stores/slices/popupSlice';
import useLadyService from '@/services/LadyService';
import { Popup } from '../index';
import { LoadingButton } from '../../ui';
import { setProfileStatus } from '@/stores/slices/userProfileSlice';
import showToast from '@/components/toast/Toast';

const Authorization = ({ forceOpen = false }) => {
	const { t, dispatch, lang, userType } = useLadyService();
	const isVideoChatMode = useSelector(getVideoChatMode);
	const isFilterMode = useSelector(getFilterMode);

	const isErocontentAccessMode = useSelector(getErocontentAccessStatus);
	const isAviableForUsers = useSelector(getAviableForUsersStatus);
	const [formErrors, setFormErrors] = useState({});
	const authorizationValue = useSelector(getAuthorizationStatus);
	const authorisationAccessTypeValue = useSelector(
		getAuthorisationAccessType,
	);

	const toggleChange = (e) => {
		let value = e.target.value;
		setValue('login', value.trim());
		if (/^\d/.test(value)) {
			const result = (value = '' + value);
			e.target.value = result;
		}
	};

	const formSchema = object().shape({
		login: mixed()
			.test(
				'email-or-phone-not-empty',
				`${t('loginisness')}`,
				(value) => {
					return value !== '';
				},
			)
			.test('is-email-or-phone', `${t('badformat')}`, (value) => {
				const phoneRegex = /^\+?\d+$/;
				const isPhone = phoneRegex.test(value.replace(/\s/g, ''));

				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				const isEmail = emailRegex.test(value);

				return isPhone || isEmail;
			}),
		password: string().required(`${t('passisness')}`),
	});

	const {
		register,
		reset,
		handleSubmit,
		formState: { errors },
		setValue,
		clearErrors,
	} = useForm({
		mode: 'onSubmit',
		resolver: yupResolver(formSchema),
	});
	const [loading, setLoading] = useState(false);

	const uuiCode = localStorage.getItem('uui');

	const [user, setUser] = useState('default');

	const onSubmit = async (data) => {
		setLoading(true);
		await axios
			.post(`${baseUrl}auth/login`, {
				identity: data.login.replace(/[" "+]/g, ''),
				password: data.password,
				...(uuiCode && { publish_code: uuiCode }),
			})
			.then((response) => {
				if (response.status === 200) {
					if (isErocontentAccessMode) {
						dispatch(setErocontentAccessStatus(true));
					}
					const token = response.data.token;
					localStorage.setItem('user', token);
					axios
						.request({
							headers: {
								Authorization: `Bearer ${token}`,
							},
							method: 'GET',
							url: `${baseUrl}user/me`,
						})
						.then(async (response) => {
							if (response.status === 200) {
								localStorage.removeItem('uui');
								const userType = response.data.data.user_type;
								const userId = response.data.data.id;

								// 2 => as here
								const profiles_status =
									response.data.data.profiles_status;

								dispatch(
									setProfileStatus(profiles_status ?? false),
								);

								// 1 => better approach is to ask to modify user/me request to set => 2
								await makeRequest({
									route: `user/info`,
									method: 'GET',
								}).then((res) => {
									dispatch(
										setProfileStatus(
											res?.data?.profiles_status ?? false,
										),
									);
								});

								dispatch(setUserType(userType));

								dispatch(setUserId(userId));
								setUser(userType);
								dispatch(setAfterAuth(true));

								try {
									const currentToken =
										localStorage.getItem('user');

									const arrFavorites = JSON.parse(
										localStorage.getItem('favorites'),
									);

									if (!!arrFavorites.length) {
										axios
											.request({
												headers: {
													Authorization: `Bearer ${currentToken}`,
												},
												method: 'POST',
												url: `${baseUrl}user/favorites/import`,
												data: {
													profiles: arrFavorites,
												},
											})
											.then((response) => {
												if (response.status === 200) {
													localStorage.removeItem(
														'favorites',
													);
												}
											})
											.catch((error) => {
												console.error(error);
												const res = error.responce;

												showToast({
													message: t('oops'),
													subMessage: `server error ${res?.status}`,
													variant: `error`,
												});
											});
									}
								} catch (error) {}
							}
						})
						.catch((error) => {
							console.error(error);
							const res = error.responce;

							showToast({
								message: t('oops'),
								subMessage: `server error ${res?.status}`,
								variant: `error`,
							});
						});
					onClose();
				}
			})
			.catch((error) => {
				if (error.response.status === 503) {
					setFormErrors({
						login: {
							type: 'is-email-or-phone',
							message: t('wrongpass'),
						},
					});
				} else if (error.response.status === 500) {
					setFormErrors({
						login: {
							type: 'is-email-or-phone',
							message: t('somethingwrong'),
						},
					});
				} else if (error.response.status === 403) {
					setFormErrors({
						login: {
							type: 'is-email-or-phone',
							message: `${t('client')} ${t('block')}`,
						},
					});
				} else {
					console.error(error);
					const res = error.responce;

					showToast({
						message: t('oops'),
						subMessage: `server error ${res?.status}`,
						variant: `error`,
					});
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const navigate = useNavigate();
	const navigateToRegistration = () => {
		dispatch(setAuthorizationStatus(false));
	};

	const onClose = () => {
		reset();
		dispatch(setBurgerMenu(false));
		dispatch(setAuthorizationStatus(false));
		dispatch(setVideoChatMode(false));
		dispatch(setFilterMode(false));
		dispatch(setMapMode(false));
		dispatch(setAviableForUsersStatus(false));
		dispatch(setAuthorisationAccessType(''));
		setFormErrors({});
		if (user !== 'default' && !loading) {
			navigate('/');
		}
	};

	const navigateToForgorPass = () => {
		reset();
		dispatch(setForgotPasswordStatus(true));
		dispatch(setAuthorizationStatus(false));
	};

	const checkTitle = () => {
		if (isVideoChatMode) {
			return t('videochat');
		} else if (authorisationAccessTypeValue === 'ero') {
			return t('erocontent');
		} else if (authorisationAccessTypeValue === 'support') {
			return t('support');
		} else if (authorisationAccessTypeValue === 'note') {
			return t('note');
		} else if (authorisationAccessTypeValue === 'order') {
			return t('onlineorder');
		} else if (authorisationAccessTypeValue === 'interaction') {
			return t('adsinteraction');
		} else if (authorisationAccessTypeValue === 'complain') {
			return t('complain');
		} else if (authorisationAccessTypeValue === 'review') {
			return t('writereview');
		} else if (authorisationAccessTypeValue === 'preview') {
			return t('modelpreviews');
		} else {
			return t('login');
		}
	};
	const [passValue, setPassValue] = useState('');
	useEffect(() => {
		if (authorizationValue || forceOpen) {
			setValue('password', passValue);
		}
	}, [authorizationValue, passValue, forceOpen]);

	return (
		<>
			<Popup
				open={!!authorizationValue || forceOpen}
				setOpen={onClose}
				id={'authorization'}
				wrapperClazz="authorization"
				closeOutside={false}
				onSubmit={handleSubmit(onSubmit)}
			>
				<h2 className="mb-32">{checkTitle()}</h2>
				{isVideoChatMode && (
					<>
						<p className="p2 text-center">
							<b>{t('aviableforusers')}</b>
						</p>
						<p className="p2 text-center">{t('meetmodelonline')}</p>
					</>
				)}
				{isFilterMode && (
					<p className="p2 text-center">
						<b>{t('savefiltersincab')}</b>
					</p>
				)}
				{(isAviableForUsers ||
					authorisationAccessTypeValue === 'ero' ||
					authorisationAccessTypeValue === 'note' ||
					authorisationAccessTypeValue === 'support' ||
					authorisationAccessTypeValue === 'order' ||
					authorisationAccessTypeValue === 'interaction' ||
					authorisationAccessTypeValue === 'complain' ||
					authorisationAccessTypeValue === 'preview' ||
					authorisationAccessTypeValue === 'review') && (
					<p className="p2 text-center">
						<b>{t('aviableforusers')}</b>
					</p>
				)}
				<div className="popup-form__inner">
					<fieldset>
						<legend>{t('loginuser')}</legend>
						<InputInLabel
							clazz={`authorization__password${
								errors.login ? ' _error' : ''
							}`}
							id={'loginForm'}
							title={t('loginuser')}
							type={'text'}
							autocomplete={'login'}
							onChange={(e) => {
								if (errors.login) {
									clearErrors('login');
								}
								if (formErrors?.login?.message) {
									setFormErrors({});
								}
								clearErrors('root');
								toggleChange(e);
							}}
							placeholder={'email@gmail.com / +420XXXXXXXXX'}
							register={{ ...register('login') }}
						/>
					</fieldset>

					<fieldset>
						<legend>{t('pass')}</legend>
						<InputInLabel
							clazz={`authorization__password${
								errors.password ? ' _error' : ''
							}`}
							id={'passwordForm'}
							type={'password'}
							onChange={(e) => {
								if (errors.password) {
									clearErrors('password');
								}
								if (formErrors?.login?.message) {
									setFormErrors({});
								}
								clearErrors('root');
								setPassValue(e.target.value);
							}}
							autocomplete={'password'}
							placeholder={'••••••••'}
							register={{ ...register('password') }}
						/>
					</fieldset>

					{(errors.password && (
						<p className={'popup-form__error'}>
							{errors.password.message}
						</p>
					)) ||
						((formErrors.login || errors.login) && (
							<p className={'popup-form__error'}>
								{formErrors.login?.message ||
									errors.login.message}
							</p>
						))}

					<button
						type={'button'}
						className={'authorization__forgot'}
						onClick={() => navigateToForgorPass()}
					>
						{t('recoverpassword') + '?'}
					</button>

					<LoadingButton
						isLoading={loading}
						size={'l'}
						buttonType={'submit'}
						clazz={'button--green authorization__submit'}
					>
						{t('login')}
					</LoadingButton>
				</div>
				<div className="popup-form__box ">
					{isAviableForUsers ? (
						<>
							<span className="mt-8">{t('or')}</span>
							<ButtonLink
								size={'l'}
								target={'_blank'}
								href={`/${lang}/registration`}
								clazz={
									'button button_outline--green no-hover-bg w-100 mt-8'
								}
								onClick={() => onClose()}
							>
								{t('register')}
							</ButtonLink>
						</>
					) : (
						<>
							<span className="mt-24">{t('noaccount')}</span>
							<ButtonLink
								size={'l'}
								clazz={
									'button button_outline--green no-hover-bg w-100 mt-8'
								}
								href={`/${lang}/registration`}
								onClick={() => onClose()}
							>
								{t('register')}
							</ButtonLink>
						</>
					)}
				</div>
				<p className="m-auto mt-20 capitalize">
					{t('needhelp')}?{' '}
					<a
						href={`https://wa.me/420774231617?text=Hello! I have a question / problem on website (User agent: ${navigator.userAgent})`}
						target="_blank"
					>
						{t('support')}
					</a>
					.
				</p>
			</Popup>
		</>
	);
};

export default Authorization;
